/**
 * Imaging & Fax tab aggregates from studies, faxes, vitalrounds, referrals.
 */

import { getActivityCount, STUDY_METRIC_FIELDS } from '../../utils/chartBinning';
import { DIRECTION_INBOUND, rowMatchesFacilityScope } from '../../utils/referralDirection';
import { isQuickReferral } from '../../utils/referralTypeLabel';
import { pctChange } from './overviewCompute';

function f(record) {
  return record?.data || record?.Data || {};
}

function facKey(id) {
  if (id == null || id === '') return '';
  const n = Number(id);
  if (Number.isFinite(n)) return String(Math.round(n));
  return String(id).trim();
}

function inHubFacility(record, hubFacIds, dataType) {
  if (!hubFacIds?.size) return true;
  const data = f(record);
  if (dataType === 'referrals' || dataType === 'vitalrounds') {
    return rowMatchesFacilityScope(data, dataType, hubFacIds, DIRECTION_INBOUND);
  }
  const fid = facKey(data.facility_id ?? data.receiving_facility_id);
  return fid && hubFacIds.has(fid);
}

function sumStudies(records, hubFacIds, fields) {
  let total = 0;
  for (const r of records || []) {
    if (!inHubFacility(r, hubFacIds, 'studies')) continue;
    total += getActivityCount(r, 'studies', fields);
  }
  return total;
}

export function computeImagingTab({
  studiesCurrent,
  studiesPrior,
  faxesCurrent,
  vitalroundsCurrent,
  vitalroundsPrior,
  referralsCurrent,
  hubFacIds,
  metricsStudies,
  metricsStudiesPrior,
  metricsRounds,
  metricsRoundsPrior,
}) {
  const studiesIn = sumStudies(studiesCurrent, hubFacIds, STUDY_METRIC_FIELDS.received);
  const studiesOut = sumStudies(studiesCurrent, hubFacIds, STUDY_METRIC_FIELDS.sent);
  const studiesTotal = metricsStudies != null
    ? Number(metricsStudies) || 0
    : studiesIn + studiesOut;
  const studiesPriorTotal = metricsStudiesPrior != null
    ? Number(metricsStudiesPrior) || 0
    : sumStudies(studiesPrior, hubFacIds, STUDY_METRIC_FIELDS.total);

  let faxPages = 0;
  let faxCount = 0;
  for (const r of faxesCurrent || []) {
    if (!inHubFacility(r, hubFacIds, 'faxes')) continue;
    faxCount += 1;
    const pages = Number(f(r).num_pages);
    faxPages += Number.isFinite(pages) ? pages : 1;
  }

  let rounds = 0;
  if (metricsRounds != null) rounds = Number(metricsRounds) || 0;
  else {
    for (const r of vitalroundsCurrent || []) {
      if (inHubFacility(r, hubFacIds, 'vitalrounds')) rounds += 1;
    }
  }
  let roundsPrior = 0;
  if (metricsRoundsPrior != null) roundsPrior = Number(metricsRoundsPrior) || 0;
  else {
    for (const r of vitalroundsPrior || []) {
      if (inHubFacility(r, hubFacIds, 'vitalrounds')) roundsPrior += 1;
    }
  }

  let quickRefer = 0;
  let externalSender = 0;
  let withDicom = 0;
  let refTotal = 0;
  for (const r of referralsCurrent || []) {
    if (!inHubFacility(r, hubFacIds, 'referrals')) continue;
    refTotal += 1;
    const data = f(r);
    if (isQuickReferral(data)) quickRefer += 1;
    const nonVe = data.is_referred_by_non_vital_user;
    if (nonVe === 1 || nonVe === '1' || nonVe === true) externalSender += 1;
    const dicom = Number(data.no_of_DICOM ?? data.no_of_dicom);
    if (Number.isFinite(dicom) && dicom > 0) withDicom += 1;
  }

  return {
    studiesTotal,
    studiesDelta: pctChange(studiesTotal, studiesPriorTotal),
    studiesIn,
    studiesOut,
    faxPages,
    faxCount,
    rounds,
    roundsDelta: pctChange(rounds, roundsPrior),
    quickRefer,
    externalSender,
    withDicom,
    dicomShare: refTotal ? withDicom / refTotal : 0,
    refTotal,
    // Explicitly unavailable
    pacsStatus: null,
    emrPush: null,
  };
}
